"use client";

import React from "react";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { usePathname } from "next/navigation";
import { useMediaQuery, useTheme, Box } from "@mui/material";

const capitalize = (str: string) => str
    .replace(/-/g, " ")
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

interface BreadcrumbProps {
    title?: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ title }) => {
    const pathname = usePathname() || "";
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const paths = pathname?.split("/").filter(Boolean) || [];
    
    // Skip the locale segment (first segment like 'en-us', 'en-ca', etc.)
    const filteredPaths = paths.slice(1);

    // Build the breadcrumb items
    const breadcrumbs = filteredPaths.map((segment, idx) => {
        const isLast = idx === filteredPaths.length - 1;
        const href = `/${paths[0]}/${filteredPaths.slice(0, idx + 1).join("/")}`;
        // Keep the last segment (label) as-is from API, capitalize intermediate segments
        const label = isLast 
            ? decodeURIComponent(segment)
            : capitalize(decodeURIComponent(segment));
        
        if (isLast) {
          return (
            <span
              key={href}
              style={{ color: "var(--theme-palette-text-primary)", fontWeight: 500 }}
              data-testid="breadcrumb-navigation-current-page"
            >
              {title || label}
            </span>
          );
        }
        return (
            <Link
                key={href}
                href={href}
                color="text.primary"
                sx={{ fontWeight: 500, textDecoration:"underline !important"}}
                data-testid={`breadcrumb-navigation-link_${idx + 1}`}
            >
                {label}
            </Link>
        );
    });

    // Always add Home at the start
    breadcrumbs.unshift(
      <Link
        key="/"
        href={`/${paths[0] || ""}`}
        color="text.primary"
        sx={{ fontWeight: 500, textDecoration: "underline !important" }}
        data-testid="breadcrumb-navigation-link_0"
      >
        Home
      </Link>
    );

    // Handle mobile truncation for tour pages
    // Pattern: Home › Tours › South America › Argentina › Patagonia (only last item truncated)
    if (isMobile && breadcrumbs.length > 3) {
        const isTourPage = pathname?.includes("/tours");
        
        if (isTourPage && breadcrumbs.length >= 4) {
            // Show all breadcrumbs with only the last item truncated
            return (
                <Box sx={{ paddingBottom: { xs: "24px", md: "32px" } }}>
                    <Breadcrumbs 
                        separator="›" 
                        aria-label="breadcrumb"
                        sx={{
                            fontSize: "12px",
                            "& .MuiBreadcrumbs-ol": {
                                flexWrap: "nowrap",
                                overflow: "hidden"
                            },
                            "& .MuiBreadcrumbs-li": {
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize: "12px"
                            },
                            "& .MuiBreadcrumbs-li:not(:last-of-type)": {
                                flexShrink: 0,
                                maxWidth: "none"
                            },
                            "& .MuiBreadcrumbs-li:last-of-type": {
                                minWidth: 0,
                                maxWidth: "120px"
                            }
                        }}
                    >
                        {breadcrumbs}
                    </Breadcrumbs>
                </Box>
            );
        }
    }

    return (
        <Box
            sx={{ 
                paddingBottom: { xs: "24px", md: "32px" }
            }}
            component="section"
            data-track="visibility"
            data-parent-container-type="pdp_breadcrumb"
            data-module-name="pdp_breadcrumb"
            data-ranking-algorithm="manual"
            data-module-ranking-position="1"
            data-testid="pdp-breadcrumb-section-root"
        >
            <Breadcrumbs
                separator="›"
                aria-label="breadcrumb"
                data-testid="pdp-breadcrumbs"
                sx={{
                    fontSize: "12px",
                    "& .MuiBreadcrumbs-ol": {
                        flexWrap: "nowrap",
                        overflow: "hidden"
                    },
                    "& .MuiBreadcrumbs-li": {
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: "12px"
                    },
                    "& .MuiBreadcrumbs-li:first-of-type": {
                        flexShrink: 0,
                        maxWidth: "none"
                    },
                    "& .MuiBreadcrumbs-li:not(:first-of-type)": {
                        maxWidth: isMobile ? "100px" : "none"
                    }
                }}
            >
                {breadcrumbs}
            </Breadcrumbs>
        </Box>
    );
};

export default Breadcrumb;